import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ArrowRight, Leaf, CalendarDays } from "lucide-react";
import seasonalGuideImage from "@/assets/category-seasonal-guide.webp";
import heroLifestyle from "@/assets/hero-lifestyle-garden.webp";
import { getCurrentMonth } from "@/lib/seasonalHelpers";

const Hero = () => {
  const currentMonth = getCurrentMonth();
  const monthSlug = currentMonth.toLowerCase();

  return (
    <section className="border-b border-border/60 bg-muted/30">
      <div className="container mx-auto px-4 lg:px-8 py-8 md:py-12">
        <div className="grid gap-5 lg:grid-cols-3">
          {/* Brand promise */}
          <div className="relative lg:col-span-2 overflow-hidden rounded-2xl border border-border/60 shadow-soft flex flex-col justify-center">
            <img
              src={heroLifestyle}
              alt="A gardener tending to plants in a lush British garden in golden afternoon light"
              width={1200}
              height={630}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-linear-to-r from-black/80 via-black/65 to-black/40" />
            <div className="relative z-10 p-7 md:p-10">
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
                <Leaf className="h-3.5 w-3.5 text-primary" /> UK gardening, tested and trusted
              </span>
              <h1 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.08] tracking-tight text-white drop-shadow-md">
                Practical advice for real British gardens
              </h1>
              <p className="mt-4 max-w-xl text-base md:text-lg leading-relaxed text-white/90">
                Month-by-month jobs, plant guides written for the UK climate and
                hands-on reviews of the kit we actually use.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button size="lg" variant="cta" asChild>
                  <Link to="/plants">
                    Browse plant guides
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/category/$categorySlug" params={{ categorySlug: "reviews" }}>See our tested picks</Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-5">
            {/* Seasonal tile */}
            <Link
              to="/category/$categorySlug"
              params={{ categorySlug: "seasonal-guide" }}
              search={{ sub: monthSlug } as never}
              className="group relative overflow-hidden rounded-2xl border border-border/60 shadow-soft min-h-[190px]"
            >
              <img
                src={seasonalGuideImage}
                alt=""
                width={600}
                height={400}
                loading="eager"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/45 to-black/10" />
              <div className="relative z-10 flex h-full flex-col justify-end p-5">
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground">
                  <CalendarDays className="h-3 w-3" /> This month
                </span>
                <h2 className="mt-2 font-display text-xl font-semibold text-white drop-shadow">
                  What to do in the garden in {currentMonth}
                </h2>
                <span className="mt-1 inline-flex items-center text-sm font-semibold text-white/90">
                  Read the {currentMonth} guide
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>

            {/* Featured scarifying guide */}
            <Link
              to="/$slug"
              params={{ slug: "scarifiers" }}
              className="group rounded-2xl border border-border/60 bg-card p-5 shadow-soft block"
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-cta/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-cta">
                <Leaf className="h-3 w-3" /> Lawn care
              </span>
              <div className="mt-3 min-w-0">
                <h2 className="font-display text-base font-semibold text-foreground">
                  Scarifying: all you need to know
                </h2>
                <p className="mt-1 text-sm leading-snug text-muted-foreground">
                  When to scarify, how to tackle moss and thatch, and which scarifier
                  suits your lawn. Compare electric, cordless and petrol picks.
                </p>
                <span className="mt-2 inline-flex items-center text-sm font-semibold text-cta">
                  Find the best scarifier
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
